import { useMemo, useState } from 'react';
import { Grid, Typography, Switch } from '@mui/material';

// our components
import ColumnItem from 'components/DataTable/ColumnItem';
import ColumnVerticalHeader from 'components/DataTable/ColumnVerticalHeader';
import DataTable from 'components/DataTable/DataTable';
import FreemiumCheckBox from 'components/Checkbox/FreemiumCheckBox';
import LoadingSkeleton from 'components/Frame/LoadingSkelton';
import SearchClients from 'components/Input/Clients/SearchClients';

// our hooks
import useGetAllFreemiumAdvisors from 'hooks/freemium/useGetAllFreemiumAdvisors';

function FreemiumTable() {
	const [paying, setPaying] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');

	const {
		isLoading: isLoadingFreemiumAdvisors,
		isSuccess: isSuccessFreemiumAdvisors,
		data: freemiumAdvisors
	} = useGetAllFreemiumAdvisors(paying);

	const freemiumAdvisorsList = useMemo(() => {
		if (freemiumAdvisors) {
			return freemiumAdvisors
				.map((advisor) => {
					return {
						...advisor,
						id: advisor.userId
					};
				})
				.filter((advisor) => {
					const fieldsOfInterest = [
						advisor.firstName,
						advisor.lastName,
						advisor.advisorEmail
					];

					return fieldsOfInterest.some((fieldValue) =>
						fieldValue
							.toLowerCase()
							.includes(searchTerm.toLowerCase())
					);
				});
		}
		return [];
	}, [freemiumAdvisors, searchTerm]);

	const firstNameKey = 'firstName';
	const lastNameKey = 'lastName';
	const advisorEmailKey = 'advisorEmail';
	const createdKey = 'created';
	const companyKey = 'companyName';

	const columnsAdvisors = [
		ColumnItem(firstNameKey, 'First Name', 0.6, {
			renderHeader: ({ colDef }) => (
				<ColumnVerticalHeader headerName={colDef.headerName} />
			)
		}),
		ColumnItem(lastNameKey, 'Last Name', 0.6, {
			renderHeader: ({ colDef }) => (
				<ColumnVerticalHeader headerName={colDef.headerName} />
			)
		}),
		ColumnItem(advisorEmailKey, 'Email', 1, {
			renderHeader: ({ colDef }) => (
				<ColumnVerticalHeader headerName={colDef.headerName} />
			)
		}),
		ColumnItem(companyKey, 'Company', 0.6, {
			renderHeader: ({ colDef }) => (
				<ColumnVerticalHeader headerName={colDef.headerName} />
			)
		}),
		ColumnItem(createdKey, 'Created', 1, {
			renderHeader: ({ colDef }) => (
				<ColumnVerticalHeader headerName={colDef.headerName} />
			)
		}),
		ColumnItem('actions', 'Is Paying', 0.5, {
			renderHeader: ({ colDef }) => (
				<ColumnVerticalHeader headerName={colDef.headerName} />
			),
			renderCell: ({ row }) => <FreemiumCheckBox row={row} />
		})
	];

	if (isLoadingFreemiumAdvisors) {
		return <LoadingSkeleton sx={{ width: '100%' }} />;
	}

	if (isSuccessFreemiumAdvisors) {
		return (
			<>
				<Grid container item xs={12} sx={{ alignItems: 'center' }}>
					<Grid
						container
						item
						xs={12}
						md={3}
						xl={2}
						sx={{ alignItems: 'center' }}
					>
						<Typography variant="h6">
							{paying ? 'Paying Advisors' : 'Free Advisors'}
						</Typography>
						<Switch
							checked={paying}
							onChange={(e) => setPaying(e.target.checked)}
							color="primary"
						/>
					</Grid>
					<Grid
						container
						item
						xs={12}
						md={9}
						xl={10}
						sx={{ justifyContent: 'flex-end' }}
					>
						<Grid item xs={12} md={6} xl={3}>
							<SearchClients
								debounceCallBack={setSearchTerm}
								placeholder="Search Advisors"
							/>
						</Grid>
					</Grid>
				</Grid>
				<Grid item xs={12}>
					<DataTable
						columns={columnsAdvisors}
						rows={freemiumAdvisorsList}
						disableSelectionOnClick
						rowsPerPageOptions={10}
						handleRowClick={() => {}}
						withRowColorCheck
					/>
				</Grid>
			</>
		);
	}
}

export default FreemiumTable;
